/**
 * PushNotificationsCard — card em /perfil/notificacoes pra gerenciar push
 * nativo do dispositivo atual.
 *
 * R6.5 / FRENTE E (2026-05-25).
 *
 * Estados visíveis:
 *   - Sem suporte: alerta cinza com motivo (ex: iOS sem PWA instalado).
 *   - Suportado + não subscrito: botão "Ativar notificações no celular".
 *   - Subscrito: status "Ativo (N devices)" + botão "Desativar".
 *   - Permission denied: alerta cinza "Você bloqueou — habilite manualmente".
 *
 * Toast feedback em sucess/error pra todas ações.
 */

import { useEffect, useState } from 'react';

import { Alert, Button, Card, useToast } from '@/components/ui';
import {
  getPushStatus,
  requestPushPermission,
  unsubscribeFromPush,
  type PushStatus,
  type PushUnsupportedReason,
} from '@/lib/push-subscription';

const UNSUPPORTED_MESSAGES: Record<PushUnsupportedReason, string> = {
  'no-notification-api': 'Seu navegador não suporta notificações. Tente Chrome, Edge ou Firefox.',
  'no-push-manager': 'Seu navegador não suporta push notifications.',
  'no-service-worker': 'Service Worker não suportado — atualize o navegador.',
  'no-vapid-key': 'Push notifications não configurado no servidor — fale com o admin.',
  'permission-denied':
    'Você bloqueou notificações. Habilite manualmente nas configurações do navegador.',
  'ios-needs-install':
    'No iPhone, primeiro instale o IGM como ícone na tela inicial: toque no ícone de compartilhar e escolha "Adicionar à Tela de Início".',
};

export function PushNotificationsCard() {
  const { show: showToast } = useToast();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [pending, setPending] = useState(false);

  // Carrega status no mount
  useEffect(() => {
    let cancelled = false;
    void getPushStatus().then((s) => {
      if (!cancelled) setStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status) {
    return (
      <Card variant="flat" className="mb-4">
        <div className="p-3 text-sm text-ink-3 dark:text-ink-dark-3">Carregando status…</div>
      </Card>
    );
  }

  async function handleActivate() {
    setPending(true);
    try {
      const newStatus = await requestPushPermission();
      setStatus(newStatus);
      if (newStatus.subscribedInBrowser) {
        showToast('Notificações ativadas neste dispositivo');
      } else if (newStatus.permission === 'denied') {
        showToast('Permissão negada pelo navegador', { variant: 'error' });
      }
    } catch (err) {
      console.error('[push] requestPermission failed:', err);
      showToast('Falha ao ativar notificações', { variant: 'error' });
    } finally {
      setPending(false);
    }
  }

  async function handleDeactivate() {
    setPending(true);
    try {
      const newStatus = await unsubscribeFromPush();
      setStatus(newStatus);
      showToast('Notificações desativadas neste dispositivo');
    } catch (err) {
      console.error('[push] unsubscribe failed:', err);
      showToast('Falha ao desativar notificações', { variant: 'error' });
    } finally {
      setPending(false);
    }
  }

  // Branch 1: sem suporte
  if (!status.supported) {
    return (
      <Card variant="flat" className="mb-4" data-testid="push-card-unsupported">
        <div className="p-3">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 mb-1">
            Notificações no celular
          </p>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
            Receba alertas nativos do sistema mesmo com o app fechado.
          </p>
          <Alert variant="info">
            {status.unsupportedReason
              ? UNSUPPORTED_MESSAGES[status.unsupportedReason]
              : 'Não suportado neste navegador.'}
          </Alert>
        </div>
      </Card>
    );
  }

  // Branch 2: permission denied
  if (status.permission === 'denied') {
    return (
      <Card variant="flat" className="mb-4" data-testid="push-card-denied">
        <div className="p-3">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 mb-1">
            Notificações no celular
          </p>
          <Alert variant="info">{UNSUPPORTED_MESSAGES['permission-denied']}</Alert>
        </div>
      </Card>
    );
  }

  // Branch 3: backend sem VAPID
  if (!status.pushEnabledBackend) {
    return (
      <Card variant="flat" className="mb-4" data-testid="push-card-backend-disabled">
        <div className="p-3">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 mb-1">
            Notificações no celular
          </p>
          <Alert variant="info">
            Push notifications não configurado no servidor — fale com o admin.
          </Alert>
        </div>
      </Card>
    );
  }

  // Branch 4: ativo ou pronto pra ativar
  return (
    <Card variant="flat" className="mb-4" data-testid="push-card">
      <div className="p-3 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            Notificações no celular
          </p>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
            {status.subscribedInBrowser
              ? `Ativo neste dispositivo • ${status.activeDevicesBackend} ${
                  status.activeDevicesBackend === 1 ? 'dispositivo cadastrado' : 'dispositivos cadastrados'
                }`
              : 'Receba alertas nativos mesmo com o app fechado.'}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:flex-shrink-0">
          {status.subscribedInBrowser ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleDeactivate}
              disabled={pending}
              data-testid="push-card-deactivate"
            >
              {pending ? '…' : 'Desativar'}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={handleActivate}
              disabled={pending}
              data-testid="push-card-activate"
            >
              {pending ? 'Ativando…' : 'Ativar notificações'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
